import React, { useState, useEffect, memo, useRef, useCallback } from 'react';
import { FiMapPin, FiSave, FiX, FiLoader, FiUpload, FiClock, FiChevronDown, FiChevronUp, FiAlertTriangle, FiGrid, FiKey, FiDownload, FiRefreshCw, FiNavigation, FiPlus, FiTrash2, FiArrowUp, FiArrowDown, FiGlobe } from 'react-icons/fi';
import QRCode from 'qrcode';
import api from '../services/api';
import { challengesAPI } from '../services/api';
import LanguageTabsEditor from './LanguageTabsEditor';
import { LucideIcon } from './IconPicker';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' }
];

// Legacy type mappings (kept for backwards compatibility)
const challengeTypes = {
  urban: { name: 'Urban', color: '#3B82F6', icon: '🏙️' },
  nature: { name: 'Nature', color: '#22C55E', icon: '🌲' },
  historic: { name: 'Historic', color: '#A855F7', icon: '🏛️' },
  adventure: { name: 'Adventure', color: '#EF4444', icon: '⛰️' },
  water: { name: 'Water', color: '#06B6D4', icon: '🌊' },
  park: { name: 'Park', color: '#84CC16', icon: '🌳' }
};

// Map category slug to legacy type for backwards compatibility
const categoryToTypeMap = {
  'general': 'urban',
  'nature': 'nature',
  'history': 'historic',
  'food': 'food',
  'adventure': 'adventure',
  'urban': 'urban',
  'water': 'water',
  'sports': 'adventure',
  'family': 'park',
  'art': 'museums',
  'nightlife': 'nightlife',
  'photography': 'photography'
};

const CreateChallengeModal = memo(({
  isOpen,
  onClose,
  location,
  formData,
  setFormData,
  onSubmit,
  isLoading,
  onChooseImage,
  onPickFromMap
}) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [nearbyChallenges, setNearbyChallenges] = useState([]);
  const [showTimeConstraint, setShowTimeConstraint] = useState(false);
  const [timeConstraintEnabled, setTimeConstraintEnabled] = useState(false);
  const [timeConstraintType, setTimeConstraintType] = useState('window');
  const [timeStartHour, setTimeStartHour] = useState(9);
  const [timeEndHour, setTimeEndHour] = useState(18);
  const [timeDays, setTimeDays] = useState([]);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [timeTimezone, setTimeTimezone] = useState('Europe/Berlin');

  // Route challenge state
  const [challengeMode, setChallengeMode] = useState('single_point');
  const [routeType, setRouteType] = useState('ordered');
  const [minRequired, setMinRequired] = useState(3);
  const [waypoints, setWaypoints] = useState([]);
  const [showRouteSection, setShowRouteSection] = useState(false);

  // Verification state
  const [showVerification, setShowVerification] = useState(false);
  const [verificationType, setVerificationType] = useState('gps');
  const [qrSecret, setQrSecret] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [passphraseHint, setPassphraseHint] = useState('');

  // Translation state
  const [showTranslations, setShowTranslations] = useState(false);
  const [translations, setTranslations] = useState({
    en: { name: '', description: '' },
    de: { name: '', description: '' },
    es: { name: '', description: '' }
  });
  const [availableLanguages, setAvailableLanguages] = useState([
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'de', name: 'German', nativeName: 'Deutsch' },
    { code: 'es', name: 'Spanish', nativeName: 'Español' }
  ]);

  // Categories state
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const defaultCategorySetRef = useRef(false);  // Use ref - doesn't cause re-renders

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/challenge-categories');
        if (response.data.success) {
          const enabledCategories = response.data.categories || [];
          console.log('[CreateChallenge] Categories loaded:', enabledCategories.map(c => ({ id: c.id, name: c.name, slug: c.slug })));
          setCategories(enabledCategories);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Reset category when modal closes, set default when modal opens
  // Using ref to track if default was set - refs don't trigger re-renders
  useEffect(() => {
    console.log('[CreateChallenge] useEffect triggered - isOpen:', isOpen, 'categories.length:', categories.length, 'defaultSet:', defaultCategorySetRef.current, 'currentCat:', selectedCategoryId);

    if (!isOpen) {
      // Modal closed - reset everything for next open
      console.log('[CreateChallenge] Modal closed - resetting');
      setSelectedCategoryId('');
      defaultCategorySetRef.current = false;
      return;
    }

    // Modal is open
    if (categories.length > 0 && !defaultCategorySetRef.current) {
      // First time with categories loaded - set default
      const defaultCat = categories.find(c => c.is_default);
      const firstCat = defaultCat || categories[0];
      if (firstCat) {
        console.log('[CreateChallenge] Setting default category (once):', firstCat.name, firstCat.id);
        setSelectedCategoryId(firstCat.id);
        defaultCategorySetRef.current = true;
      }
    }
  }, [isOpen, categories]);

  // Set selected category when editing a challenge (formData.category is populated for edit mode)
  useEffect(() => {
    if (formData.category?.id && categories.length > 0) {
      setSelectedCategoryId(formData.category.id);
    }
  }, [formData.category, categories]);

  // Generate QR secret on mount
  useEffect(() => {
    if (!qrSecret) {
      setQrSecret(crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36));
    }
  }, []);

  // Fetch available languages for translations
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await api.get('/cms/settings/languages');
        if (response.data?.languages?.length > 0) {
          setAvailableLanguages(response.data.languages);
          // Initialize translations structure for all languages
          const initTrans = {};
          response.data.languages.forEach(lang => {
            initTrans[lang.code] = { name: '', description: '' };
          });
          setTranslations(initTrans);
        }
      } catch (error) {
        console.log('Using default languages');
      }
    };
    fetchLanguages();
  }, []);

  // Sync English translation with formData when name/description changes
  useEffect(() => {
    setTranslations(prev => ({
      ...prev,
      en: {
        name: formData.name || '',
        description: formData.description || ''
      }
    }));
  }, [formData.name, formData.description]);

  // Generate QR code image when secret changes
  const generateQrImage = useCallback(async (secret) => {
    if (!secret) return;
    try {
      const url = await QRCode.toDataURL(secret, { width: 300, margin: 2 });
      setQrDataUrl(url);
    } catch (err) {
      console.error('QR generation error:', err);
    }
  }, []);

  useEffect(() => {
    if (['qr_code', 'gps_qr'].includes(verificationType) && qrSecret) {
      generateQrImage(qrSecret);
    }
  }, [verificationType, qrSecret, generateQrImage]);

  const regenerateQrSecret = () => {
    const newSecret = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36);
    setQrSecret(newSecret);
  };

  const downloadQrCode = () => {
    if (!qrDataUrl) return;
    const link = document.createElement('a');
    link.download = `challenge-qr-${qrSecret.substring(0, 8)}.png`;
    link.href = qrDataUrl;
    link.click();
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size must be less than 10MB');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post('/challenges/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.imageUrl) {
        console.log('[CreateChallenge] Image uploaded:', response.data.imageUrl);
        setFormData(prev => {
          const newData = { ...prev, image_url: response.data.imageUrl };
          console.log('[CreateChallenge] Updated formData.image_url:', newData.image_url);
          return newData;
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Update coordinates when location changes
  useEffect(() => {
    if (location && isOpen) {
      setFormData(prev => ({
        ...prev,
        latitude: location[0],
        longitude: location[1]
      }));
    }
  }, [location, isOpen, setFormData]);

  // Check for nearby challenges when location changes
  useEffect(() => {
    if (!location || !isOpen) {
      setNearbyChallenges([]);
      return;
    }
    const checkNearby = async () => {
      try {
        const response = await challengesAPI.getNearbyChallenges(location[0], location[1], { radius: 500, limit: 5 });
        setNearbyChallenges(response.data?.challenges || []);
      } catch (err) {
        // silently ignore
      }
    };
    checkNearby();
  }, [location, isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Waypoint helpers
  const addWaypoint = () => {
    setWaypoints(prev => [...prev, {
      name: `Waypoint ${prev.length + 1}`,
      latitude: '',
      longitude: '',
      radius: 30,
      isRequired: true
    }]);
  };

  const removeWaypoint = (index) => {
    setWaypoints(prev => prev.filter((_, i) => i !== index));
  };

  const updateWaypoint = (index, field, value) => {
    setWaypoints(prev => prev.map((wp, i) => i === index ? { ...wp, [field]: value } : wp));
  };

  const moveWaypoint = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= waypoints.length) return;
    const newWaypoints = [...waypoints];
    [newWaypoints[index], newWaypoints[newIndex]] = [newWaypoints[newIndex], newWaypoints[index]];
    setWaypoints(newWaypoints);
  };

  const validateForm = () => {
    const errors = {};

    if (!location) {
      errors.location = 'Please click on the map to select a location';
    }

    if (!formData.name.trim()) {
      errors.name = 'Challenge name is required';
    }

    if (!formData.description.trim()) {
      errors.description = 'Challenge description is required';
    }

    if (formData.radius < 10 || formData.radius > 1000) {
      errors.radius = 'Radius must be between 10 and 1000 meters';
    }

    if (!formData.difficulty || !['Easy', 'Moderate', 'Hard', 'Expert'].includes(formData.difficulty)) {
      errors.difficulty = 'Please select a valid difficulty level';
    }

    const points = parseInt(formData.points);
    if (!formData.points || isNaN(points) || points < 1 || points > 1000) {
      errors.points = 'Points must be between 1 and 1000';
    }

    // Route validation
    if (challengeMode === 'multi_point') {
      if (waypoints.length < 2) {
        errors.waypoints = 'Route challenges need at least 2 waypoints';
      } else {
        const invalidWp = waypoints.find(wp => !wp.latitude || !wp.longitude);
        if (invalidWp) {
          errors.waypoints = 'All waypoints must have valid coordinates';
        }
      }
      if (routeType === 'minimum' && (minRequired < 1 || minRequired > waypoints.length)) {
        errors.minRequired = `Minimum must be between 1 and ${waypoints.length}`;
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      points: parseInt(formData.points) || 10,
      latitude: parseFloat(location[0].toFixed(5)),
      longitude: parseFloat(location[1].toFixed(5)),
      isActive: formData.isActive !== false,
      challengeMode
    };

    // Add route challenge data
    if (challengeMode === 'multi_point') {
      submitData.routeType = routeType;
      if (routeType === 'minimum') {
        submitData.routeMinRequired = minRequired;
      }
      submitData.waypoints = waypoints.map((wp, i) => ({
        sequenceOrder: i + 1,
        name: wp.name || `Waypoint ${i + 1}`,
        latitude: parseFloat(wp.latitude),
        longitude: parseFloat(wp.longitude),
        radius: parseInt(wp.radius) || 30,
        isRequired: wp.isRequired !== false
      }));
      // Use first waypoint as primary lat/lng if available
      if (waypoints.length > 0 && waypoints[0].latitude && waypoints[0].longitude) {
        submitData.latitude = parseFloat(parseFloat(waypoints[0].latitude).toFixed(5));
        submitData.longitude = parseFloat(parseFloat(waypoints[0].longitude).toFixed(5));
      }
    }

    // Build time constraint if enabled
    if (timeConstraintEnabled) {
      const constraint = { timezone: timeTimezone };
      if (timeConstraintType === 'window') {
        constraint.type = 'window';
        constraint.start_hour = timeStartHour;
        constraint.end_hour = timeEndHour;
        if (timeDays.length > 0) constraint.days_of_week = timeDays;
      } else if (timeConstraintType === 'date_range') {
        constraint.type = 'date_range';
        if (dateStart) constraint.start_date = dateStart;
        if (dateEnd) constraint.end_date = dateEnd;
      } else if (timeConstraintType === 'combined') {
        constraint.type = 'combined';
        constraint.start_hour = timeStartHour;
        constraint.end_hour = timeEndHour;
        if (timeDays.length > 0) constraint.days_of_week = timeDays;
        if (dateStart) constraint.start_date = dateStart;
        if (dateEnd) constraint.end_date = dateEnd;
      }
      submitData.timeConstraint = constraint;
    }

    // Add verification data
    if (verificationType !== 'gps') {
      submitData.verificationType = verificationType;
      if (['qr_code', 'gps_qr'].includes(verificationType)) {
        submitData.qrSecret = qrSecret;
      }
      if (['passphrase', 'gps_passphrase'].includes(verificationType)) {
        submitData.passphrase = passphrase;
        if (passphraseHint) submitData.passphraseHint = passphraseHint;
      }
    }

    // Add translations (filter out empty ones)
    const filteredTranslations = {};
    for (const [locale, content] of Object.entries(translations)) {
      if (content.name || content.description) {
        filteredTranslations[locale] = content;
      }
    }
    if (Object.keys(filteredTranslations).length > 0) {
      submitData.translations = filteredTranslations;
    }

    // Add category_id
    if (selectedCategoryId) {
      submitData.category_id = selectedCategoryId;
    }

    // Debug logging for create challenge - use JSON.stringify to see actual values
    console.log('[CreateChallenge] SUBMIT - category_id:', submitData.category_id);
    console.log('[CreateChallenge] SUBMIT - selectedCategoryId state:', selectedCategoryId);
    console.log('[CreateChallenge] SUBMIT - name:', submitData.name);
    console.log('[CreateChallenge] SUBMIT - image_url:', submitData.image_url);

    onSubmit(submitData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Create New Challenge</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {validationErrors.location && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{validationErrors.location}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Challenge Name <span className="text-xs text-gray-400 font-normal">({formData.name?.length || 0}/40)</span></label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, name: e.target.value }));
                    if (validationErrors.name) {
                      setValidationErrors(prev => ({ ...prev, name: null }));
                    }
                  }}
                  className={`input ${validationErrors.name ? 'border-red-500 bg-red-50' : ''}`}
                  placeholder="e.g., Brandenburg Gate Visit"
                  maxLength={40}
                  required
                />
                {validationErrors.name && (
                  <p className="text-xs text-red-600 mt-1">{validationErrors.name}</p>
                )}
              </div>

              <div>
                <label className="label">Category</label>
                <div className="relative">
                  <select
                    value={selectedCategoryId}
                    onChange={(e) => {
                      const catId = e.target.value;
                      console.log('[CreateChallenge] Category changed:', catId, 'from:', selectedCategoryId);
                      setSelectedCategoryId(catId);
                      // Also set legacy type for backwards compatibility
                      const category = categories.find(c => c.id === catId);
                      if (category) {
                        console.log('[CreateChallenge] Selected category:', category.name, category.slug);
                        const legacyType = categoryToTypeMap[category.slug] || 'urban';
                        setFormData(prev => ({ ...prev, type: legacyType }));
                      }
                    }}
                    className="input pl-10"
                  >
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {/* Show selected category icon */}
                  {selectedCategoryId && categories.length > 0 && (
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <LucideIcon
                        name={categories.find(c => c.id === selectedCategoryId)?.icon || 'map-pin'}
                        size={16}
                        color={categories.find(c => c.id === selectedCategoryId)?.color || '#6B7280'}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Difficulty Level</label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value }))}
                  className="input"
                >
                  <option value="Easy">Easy</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Hard">Hard</option>
                  <option value="Expert">Expert</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Determines challenge complexity and user requirements
                </p>
              </div>

              <div>
                <label className="label">Points Reward</label>
                <input
                  type="number"
                  value={formData.points}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, points: value }));
                  }}
                  className="input"
                  min="1"
                  max="1000"
                  step="1"
                  placeholder="10"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Points earned when user completes this challenge
                </p>
              </div>
            </div>

            <div>
              <label className="label">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, description: e.target.value }));
                  if (validationErrors.description) {
                    setValidationErrors(prev => ({ ...prev, description: null }));
                  }
                }}
                className={`input ${validationErrors.description ? 'border-red-500 bg-red-50' : ''}`}
                rows={3}
                placeholder="Describe the challenge and what users need to do..."
                required
              />
              {validationErrors.description && (
                <p className="text-xs text-red-600 mt-1">{validationErrors.description}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Detection Radius (meters)</label>
                <input
                  type="number"
                  value={formData.radius}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, radius: parseInt(e.target.value) }));
                    if (validationErrors.radius) {
                      setValidationErrors(prev => ({ ...prev, radius: null }));
                    }
                  }}
                  className={`input ${validationErrors.radius ? 'border-red-500 bg-red-50' : ''}`}
                  min="10"
                  max="1000"
                  step="10"
                />
                {validationErrors.radius ? (
                  <p className="text-xs text-red-600 mt-1">{validationErrors.radius}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    Users need to be within this distance to complete the challenge
                  </p>
                )}
              </div>

              <div>
                <label className="label">NFT Reward Name</label>
                <input
                  type="text"
                  value={formData.nftRewardTemplate}
                  onChange={(e) => setFormData(prev => ({ ...prev, nftRewardTemplate: e.target.value }))}
                  className="input"
                  placeholder="e.g., Berlin Explorer: Brandenburg Gate"
                />
              </div>
            </div>

            {/* Image Selection */}
            <div className="mt-4">
              <label className="label text-xs">
                Image
                <span className="text-gray-500 ml-1">(optional)</span>
              </label>
              <div className="flex flex-col space-y-3">
                <div className="flex items-center space-x-3">
                  {/* Upload Button */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="btn btn-primary flex items-center space-x-2 disabled:opacity-50"
                  >
                    {isUploading ? (
                      <FiLoader className="w-4 h-4 animate-spin" />
                    ) : (
                      <FiUpload className="w-4 h-4" />
                    )}
                    <span>{isUploading ? 'Uploading...' : 'Upload Image'}</span>
                  </button>
                  {/* Gallery Button */}
                  <button
                    type="button"
                    onClick={onChooseImage}
                    className="btn btn-primary"
                  >
                    Choose from Gallery
                  </button>
                </div>
                {formData.image_url && (
                  <div className="flex items-start space-x-2">
                    <img
                      src={formData.image_url.startsWith('http') ? formData.image_url : `${(process.env.REACT_APP_API_URL || '').replace('/api', '')}${formData.image_url}`}
                      alt="Selected"
                      className="h-48 w-auto object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, image_url: null }))}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Active Status */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-900">Active Status</label>
                <p className="text-xs text-gray-500">Inactive challenges won't be visible to app users</p>
              </div>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, isActive: prev.isActive === false ? true : false }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.isActive === false ? 'bg-gray-300' : 'bg-green-500'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.isActive === false ? 'translate-x-1' : 'translate-x-6'
                }`} />
              </button>
            </div>

            {/* Time Restrictions */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => setShowTimeConstraint(!showTimeConstraint)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
              >
                <div className="flex items-center space-x-3">
                  <FiClock className="w-5 h-5 text-purple-600" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Time Restrictions</h4>
                    <p className="text-xs text-gray-500">
                      {timeConstraintEnabled
                        ? (timeConstraintType === 'window'
                            ? `${timeStartHour}:00 - ${timeEndHour}:00${timeDays.length > 0 ? `, ${timeDays.map(d => DAYS_OF_WEEK.find(dw => dw.value === d)?.label).join(', ')}` : ''}`
                            : timeConstraintType === 'date_range'
                              ? `${dateStart ? new Date(dateStart).toLocaleDateString() : '...'} - ${dateEnd ? new Date(dateEnd).toLocaleDateString() : '...'}`
                              : 'Combined')
                        : 'Optional'}
                    </p>
                  </div>
                </div>
                {showTimeConstraint ? <FiChevronUp className="w-4 h-4 text-gray-400" /> : <FiChevronDown className="w-4 h-4 text-gray-400" />}
              </button>

              {showTimeConstraint && (
                <div className="p-4 pt-0 space-y-3">
                  {/* Enable toggle */}
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={timeConstraintEnabled}
                      onChange={(e) => setTimeConstraintEnabled(e.target.checked)}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <span className="text-sm text-gray-700">Enable time restrictions</span>
                  </label>

                  {timeConstraintEnabled && (
                    <>
                      {/* Constraint type */}
                      <div>
                        <label className="label text-xs">Restriction Type</label>
                        <select
                          value={timeConstraintType}
                          onChange={(e) => setTimeConstraintType(e.target.value)}
                          className="input text-sm"
                        >
                          <option value="window">Time Window (daily hours)</option>
                          <option value="date_range">Date Range</option>
                          <option value="combined">Both (Time Window + Date Range)</option>
                        </select>
                      </div>

                      {/* Time Window fields */}
                      {(timeConstraintType === 'window' || timeConstraintType === 'combined') && (
                        <div className="space-y-3 p-3 bg-purple-50 rounded-lg">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Start Hour</label>
                              <select value={timeStartHour} onChange={(e) => setTimeStartHour(parseInt(e.target.value))} className="input text-sm">
                                {Array.from({length: 24}, (_, i) => (
                                  <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">End Hour</label>
                              <select value={timeEndHour} onChange={(e) => setTimeEndHour(parseInt(e.target.value))} className="input text-sm">
                                {Array.from({length: 24}, (_, i) => (
                                  <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Days of Week <span className="text-gray-400">(optional, leave empty for all days)</span></label>
                            <div className="flex flex-wrap gap-2">
                              {DAYS_OF_WEEK.map(day => (
                                <button
                                  key={day.value}
                                  type="button"
                                  onClick={() => {
                                    setTimeDays(prev => prev.includes(day.value)
                                      ? prev.filter(d => d !== day.value)
                                      : [...prev, day.value].sort()
                                    );
                                  }}
                                  className={`px-3 py-1 rounded-full text-xs font-medium border ${
                                    timeDays.includes(day.value)
                                      ? 'bg-purple-600 text-white border-purple-600'
                                      : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'
                                  }`}
                                >
                                  {day.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Date Range fields */}
                      {(timeConstraintType === 'date_range' || timeConstraintType === 'combined') && (
                        <div className="space-y-3 p-3 bg-blue-50 rounded-lg">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                              <input
                                type="date"
                                value={dateStart}
                                onChange={(e) => setDateStart(e.target.value)}
                                className="input text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                              <input
                                type="date"
                                value={dateEnd}
                                onChange={(e) => setDateEnd(e.target.value)}
                                className="input text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Timezone */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Timezone</label>
                        <select value={timeTimezone} onChange={(e) => setTimeTimezone(e.target.value)} className="input text-sm">
                          <option value="Europe/Berlin">Europe/Berlin (CET)</option>
                          <option value="Europe/Vienna">Europe/Vienna (CET)</option>
                          <option value="Europe/Zurich">Europe/Zurich (CET)</option>
                          <option value="Europe/London">Europe/London (GMT)</option>
                          <option value="UTC">UTC</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Route Challenge Mode */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => setShowRouteSection(!showRouteSection)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
              >
                <div className="flex items-center space-x-3">
                  <FiNavigation className="w-5 h-5 text-indigo-600" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Challenge Mode</h4>
                    <p className="text-xs text-gray-500">
                      {challengeMode === 'multi_point'
                        ? `Route (${waypoints.length} waypoints) - ${routeType === 'ordered' ? 'Ordered' : routeType === 'unordered' ? 'Collect All' : `Min ${minRequired}`}`
                        : 'Single Point'}
                    </p>
                  </div>
                </div>
                {showRouteSection ? <FiChevronUp className="w-4 h-4 text-gray-400" /> : <FiChevronDown className="w-4 h-4 text-gray-400" />}
              </button>

              {showRouteSection && (
                <div className="p-4 pt-0 space-y-3">
                  {/* Mode Toggle */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setChallengeMode('single_point')}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        challengeMode === 'single_point'
                          ? 'bg-indigo-500 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FiMapPin className="w-4 h-4 inline mr-1" />
                      Single Point
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setChallengeMode('multi_point');
                        if (waypoints.length === 0) {
                          setWaypoints([
                            { name: 'Start', latitude: location ? location[0].toFixed(5) : '', longitude: location ? location[1].toFixed(5) : '', radius: 30, isRequired: true },
                            { name: 'End', latitude: '', longitude: '', radius: 30, isRequired: true }
                          ]);
                        }
                      }}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        challengeMode === 'multi_point'
                          ? 'bg-indigo-500 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FiNavigation className="w-4 h-4 inline mr-1" />
                      Route Challenge
                    </button>
                  </div>

                  {challengeMode === 'multi_point' && (
                    <>
                      {/* Route Type Selector */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Route Type</label>
                        <div className="flex gap-1.5">
                          {[
                            { value: 'ordered', label: 'Ordered', desc: 'Visit in sequence 1-2-3' },
                            { value: 'unordered', label: 'Collect All', desc: 'Visit all, any order' },
                            { value: 'minimum', label: 'Minimum X', desc: 'Visit at least X of N' }
                          ].map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setRouteType(opt.value)}
                              className={`flex-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                routeType === opt.value
                                  ? 'bg-indigo-500 text-white shadow-sm'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                              title={opt.desc}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Minimum Required Input */}
                      {routeType === 'minimum' && (
                        <div className="p-3 bg-indigo-50 rounded-lg">
                          <label className="block text-xs font-medium text-indigo-800 mb-1">
                            Minimum waypoints required
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={minRequired}
                              onChange={(e) => setMinRequired(parseInt(e.target.value) || 1)}
                              min="1"
                              max={waypoints.length}
                              className="w-20 px-2 py-1.5 border border-indigo-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400"
                            />
                            <span className="text-xs text-indigo-600">of {waypoints.length} waypoints</span>
                          </div>
                          {validationErrors.minRequired && (
                            <p className="text-xs text-red-600 mt-1">{validationErrors.minRequired}</p>
                          )}
                        </div>
                      )}

                      {/* Waypoint Builder */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider">Waypoints</label>
                          <button
                            type="button"
                            onClick={addWaypoint}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          >
                            <FiPlus className="w-3 h-3" /> Add
                          </button>
                        </div>

                        {validationErrors.waypoints && (
                          <p className="text-xs text-red-600">{validationErrors.waypoints}</p>
                        )}

                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {waypoints.map((wp, index) => (
                            <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">
                                {index + 1}
                              </span>
                              <input
                                type="text"
                                value={wp.name}
                                onChange={(e) => updateWaypoint(index, 'name', e.target.value)}
                                placeholder="Name"
                                className="w-24 px-2 py-1 border border-gray-200 rounded text-xs"
                              />
                              <input
                                type="text"
                                value={wp.latitude}
                                onChange={(e) => updateWaypoint(index, 'latitude', e.target.value)}
                                placeholder="Lat"
                                className="w-20 px-2 py-1 border border-gray-200 rounded text-xs font-mono"
                              />
                              <input
                                type="text"
                                value={wp.longitude}
                                onChange={(e) => updateWaypoint(index, 'longitude', e.target.value)}
                                placeholder="Lng"
                                className="w-20 px-2 py-1 border border-gray-200 rounded text-xs font-mono"
                              />
                              <input
                                type="number"
                                value={wp.radius}
                                onChange={(e) => updateWaypoint(index, 'radius', parseInt(e.target.value) || 30)}
                                min="10"
                                max="500"
                                className="w-14 px-1 py-1 border border-gray-200 rounded text-xs"
                                title="Radius (m)"
                              />
                              <div className="flex items-center gap-0.5">
                                <button type="button" onClick={() => moveWaypoint(index, -1)} disabled={index === 0}
                                  className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30" title="Move up">
                                  <FiArrowUp className="w-3 h-3" />
                                </button>
                                <button type="button" onClick={() => moveWaypoint(index, 1)} disabled={index === waypoints.length - 1}
                                  className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30" title="Move down">
                                  <FiArrowDown className="w-3 h-3" />
                                </button>
                                <button type="button" onClick={() => removeWaypoint(index)}
                                  className="p-0.5 text-red-400 hover:text-red-600" title="Remove">
                                  <FiTrash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Pick from map button */}
                        {onPickFromMap && (
                          <button
                            type="button"
                            onClick={() => {
                              onPickFromMap((points) => {
                                setWaypoints(points.map((p, i) => ({
                                  name: `Waypoint ${i + 1}`,
                                  latitude: String(p.lat),
                                  longitude: String(p.lng),
                                  radius: 30,
                                  isRequired: true
                                })));
                                setChallengeMode('multi_point');
                              });
                            }}
                            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50"
                          >
                            <FiMapPin className="w-3 h-3" /> Pick from Map
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Verification Method */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => setShowVerification(!showVerification)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
              >
                <div className="flex items-center space-x-2">
                  {['qr_code', 'gps_qr'].includes(verificationType) ? (
                    <FiGrid className="w-4 h-4 text-green-600" />
                  ) : ['passphrase', 'gps_passphrase'].includes(verificationType) ? (
                    <FiKey className="w-4 h-4 text-amber-600" />
                  ) : (
                    <FiMapPin className="w-4 h-4 text-blue-600" />
                  )}
                  <span className="text-sm font-medium text-gray-900">Verification Method</span>
                  {verificationType !== 'gps' && (
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      ['qr_code', 'gps_qr'].includes(verificationType) ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {verificationType === 'qr_code' ? 'QR Code' : verificationType === 'passphrase' ? 'Passphrase' : verificationType === 'gps_qr' ? 'GPS + QR' : 'GPS + Passphrase'}
                    </span>
                  )}
                </div>
                {showVerification ? <FiChevronUp className="w-4 h-4 text-gray-400" /> : <FiChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {showVerification && (
                <div className="p-4 pt-0 space-y-3">
                  <p className="text-xs text-gray-500">How should users verify they completed this challenge?</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { value: 'gps', label: 'GPS Only', icon: '📍' },
                      { value: 'qr_code', label: 'QR Code', icon: '📱' },
                      { value: 'passphrase', label: 'Passphrase', icon: '🔑' },
                      { value: 'gps_qr', label: 'GPS + QR', icon: '📍📱' },
                      { value: 'gps_passphrase', label: 'GPS + Pass', icon: '📍🔑' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setVerificationType(opt.value)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          verificationType === opt.value
                            ? opt.value.includes('qr') ? 'bg-green-500 text-white shadow-sm'
                              : opt.value.includes('passphrase') ? 'bg-amber-500 text-white shadow-sm'
                              : 'bg-blue-500 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {/* QR Code Section */}
                  {['qr_code', 'gps_qr'].includes(verificationType) && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-green-800">QR Code Secret</span>
                        <div className="flex gap-1.5">
                          <button type="button" onClick={regenerateQrSecret} className="p-1.5 text-green-600 hover:bg-green-100 rounded" title="Regenerate">
                            <FiRefreshCw className="w-3.5 h-3.5" />
                          </button>
                          <button type="button" onClick={downloadQrCode} className="p-1.5 text-green-600 hover:bg-green-100 rounded" title="Download QR">
                            <FiDownload className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <input
                        type="text"
                        value={qrSecret}
                        readOnly
                        className="input text-xs font-mono bg-white"
                      />
                      {qrDataUrl && (
                        <div className="flex justify-center">
                          <img src={qrDataUrl} alt="QR Code" className="w-40 h-40 border border-green-200 rounded" />
                        </div>
                      )}
                      <p className="text-xs text-green-600">Print this QR code and place it at the challenge location. Users scan it to complete the challenge.</p>
                    </div>
                  )}

                  {/* Passphrase Section */}
                  {['passphrase', 'gps_passphrase'].includes(verificationType) && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                      <span className="text-sm font-medium text-amber-800">Secret Passphrase</span>
                      <input
                        type="text"
                        value={passphrase}
                        onChange={(e) => setPassphrase(e.target.value)}
                        placeholder="Enter the secret passphrase..."
                        className="input text-sm"
                      />
                      <input
                        type="text"
                        value={passphraseHint}
                        onChange={(e) => setPassphraseHint(e.target.value)}
                        placeholder="Hint for users (optional, e.g. 'Look at the sign near the entrance')"
                        className="input text-sm"
                      />
                      <p className="text-xs text-amber-600">Users must enter this exact phrase (case-insensitive) to complete the challenge.</p>
                    </div>
                  )}

                  {verificationType !== 'gps' && !['gps_qr', 'gps_passphrase'].includes(verificationType) && (
                    <p className="text-xs text-gray-500 italic">GPS verification is disabled. The challenge location will show on the map but users won't need to be physically present.</p>
                  )}
                </div>
              )}
            </div>

            {/* Translations Section */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => setShowTranslations(!showTranslations)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
              >
                <div className="flex items-center space-x-3">
                  <FiGlobe className="w-5 h-5 text-blue-500" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Translations</h4>
                    <p className="text-xs text-gray-500">
                      {Object.keys(translations).filter(l => l !== 'en' && (translations[l]?.name || translations[l]?.description)).length} language(s) translated
                    </p>
                  </div>
                </div>
                {showTranslations ? <FiChevronUp className="w-4 h-4 text-gray-400" /> : <FiChevronDown className="w-4 h-4 text-gray-400" />}
              </button>

              {showTranslations && (
                <div className="p-4 pt-0">
                  <LanguageTabsEditor
                    languages={availableLanguages}
                    defaultLang="en"
                    translations={translations}
                    fields={[
                      { key: 'name', label: 'Challenge Name', type: 'input', placeholder: 'e.g., Brandenburg Gate Visit' },
                      { key: 'description', label: 'Description', type: 'textarea', rows: 3, placeholder: 'Describe the challenge...' }
                    ]}
                    onChange={(locale, field, value) => {
                      setTranslations(prev => ({
                        ...prev,
                        [locale]: { ...prev[locale], [field]: value }
                      }));
                      // Also update formData for English
                      if (locale === 'en') {
                        setFormData(prev => ({ ...prev, [field]: value }));
                      }
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Edit English (default) content in the fields above. Add translations for other languages here.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <FiMapPin className="w-5 h-5 text-blue-600" />
                <h4 className="text-sm font-medium text-gray-900">Challenge Location</h4>
              </div>
              {location ? (
                <div className="text-sm text-gray-600">
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-green-700 font-medium text-xs">Location Selected</span>
                  </div>
                  <p><span className="font-medium">Latitude:</span> {location[0].toFixed(5)}</p>
                  <p><span className="font-medium">Longitude:</span> {location[1].toFixed(5)}</p>
                  <p className="text-xs text-gray-500 mt-1">Click on the map to change location</p>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                  <p className="text-sm text-orange-600">Click on the map to select a location first</p>
                </div>
              )}
            </div>

            {/* Nearby Challenges Warning */}
            {nearbyChallenges.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <FiAlertTriangle className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">
                    {nearbyChallenges.length} challenge{nearbyChallenges.length !== 1 ? 's' : ''} nearby
                  </span>
                </div>
                <div className="space-y-1">
                  {nearbyChallenges.map(c => (
                    <div key={c.id} className="text-xs text-amber-700 flex items-center justify-between">
                      <span className="truncate">{c.name}</span>
                      <span className="text-amber-500 ml-2 flex-shrink-0">{c.distance}m away</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary flex items-center space-x-2"
              >
                {isLoading ? (
                  <FiLoader className="w-4 h-4 animate-spin" />
                ) : (
                  <FiSave className="w-4 h-4" />
                )}
                <span>Create Challenge</span>
              </button>
            </div>
          </form>
        </div>
        {/* End Scrollable Content */}
      </div>
    </div>
  );
});

CreateChallengeModal.displayName = 'CreateChallengeModal';

export default CreateChallengeModal;
